/* eslint-disable max-len */
/* eslint-disable consistent-return */
/* eslint-disable import/no-named-as-default */
/* eslint-disable import/no-named-as-default-member */

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Dropdown, Empty, Input, Menu, message, Popover, Row, Select, Space, Spin, Tabs, Tooltip, Typography } from 'antd';
import { CloseOutlined, ReloadOutlined, UploadOutlined, FieldTimeOutlined, SearchOutlined } from '@ant-design/icons';
import SimpleBar from 'simplebar-react';
import { FaCaretDown, FaQuoteLeft, FaRegSmile } from 'react-icons/fa';
import FileUploadModal from 'components/FileUpload/FileUploadModal';
import { EmojiPicker } from 'components/EmojiPicker';
import RichTextEditor from 'components/RichTextEditor';
import { UploadFileApi, UploadResponse, UploadS3Response } from 'types/upload-type';
import { checkIsAdmin } from 'utils/auth';
import { mentionCommentParser, getMentionUuidFromNode, deltaToHtml, parseDateToUSFormat } from 'utils/string.utils';
import apiRequests from 'utils/api';
import { debounceAsync } from 'utils/debounce';
import apiRoutes from 'config/apiRoute';
import { MdOutlineTextFormat } from 'react-icons/md';
import { IoSendSharp } from 'react-icons/io5';
import CommentCard from './CommentCard';
import TemplateCommentContainer from './TemplateCommentContainer';
import { CommendScheduleError, CommentSchedule } from './CommentSchedule';
import useComments from './useComments';
import { UserResponse } from '../../types';
import styles from './styles.module.less';

const { Text } = Typography;

export interface CommentsProps {
  withScheduled?: boolean | undefined;
  highlightUuid?: string;
  typeUuid: string;
  type: 'tasks' | 'projects' | 'requests';
  maxHeight?: string;
  maxFullHeight?: boolean;
  minHeight?: string;
  projectUuid: string;
  textEditorClassName?: string;
  scrollingContainer?: string | HTMLElement | undefined;
  hideTemplates?: boolean;
  isProjectPersonal?: boolean;
  mentionTypes?: ('regular_user' | 'freelancer' | 'contact' | 'group')[];
  hideTabs?: any;
  replaceUserInTemplate?: UserResponse;
  canComment?: boolean;
  disableSearch?: boolean;
  onAddComment?: () => void | undefined;
  onFinishSubmit?: (values: any, images: UploadFileApi[]) => void;
  onFilesDelete?: (uuid: string[]) => void;
  onCommentsLoaded?: any;
  onImageModalChanged?: (open: boolean) => void;
  onInteractionOccurred?: () => void;
}

const Comments: FC<CommentsProps> = ({
  withScheduled,
  highlightUuid,
  typeUuid,
  type,
  maxHeight,
  minHeight,
  maxFullHeight,
  projectUuid,
  textEditorClassName = '',
  scrollingContainer,
  hideTemplates,
  isProjectPersonal,
  mentionTypes,
  hideTabs,
  replaceUserInTemplate,
  canComment = true,
  disableSearch = false,
  onAddComment,
  onFinishSubmit,
  onFilesDelete,
  onCommentsLoaded,
  onImageModalChanged,
  onInteractionOccurred,
}) => {
  const {
    inputRef,
    user,
    state,
    commentType,
    scrollRef,
    tabView,
    setCommentType,
    setTabView,
    setSearchTerm,
    dropdownResults,
    dropdownLoading,
    setDropdownResults,
    fetchDropdownResults,
    setComment,
    reloadComment,
    handleUpdateComment,
    handleUpdateScheduleTime,
    handleDeleteComment,
    handleAddComments,
    loadMore,
    handleDeleteUpload,
    handleUpdateReply,
    handleDeleteReply,
    handleAddReply,
    setReplyTo,
    setQuotedComment,
    handleAddReaction,
    handleRemoveReaction,
    handleRemoveReplyReaction,
    handleAddReplyReaction,
  } = useComments({
    withScheduled,
    highlightUuid,
    typeUuid,
    type,
    onAddComment,
    onCommentsLoaded,
    onFilesDelete,
    onFinishSubmit,
  });
  const [visible, setVisible] = useState(false);
  const [images, setImages] = useState<UploadFileApi[]>([]);
  const [loadingImages, setLoadingImages] = useState(0);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);

  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isEditorMultiline, setIsEditorMultiline] = useState(false);
  const [dragState, setDragState] = useState<'allowed' | 'blocked' | null>(null);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingScrollUuid, setPendingScrollUuid] = useState<string | null>(null);
  const singleLineHeightRef = useRef(38);
  const chatInputRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useRef(debounceAsync(async (value: string) => {
    fetchDropdownResults(value);
  }, 300));

  useEffect(() => {
    const timer = setTimeout(() => {
      const editorEl = inputRef.current?.getQuill()?.getEditor()?.root;
      if (!editorEl) return;
      const cs = window.getComputedStyle(editorEl);
      const lineHeight = parseFloat(cs.lineHeight) || 23;
      const paddingTop = parseFloat(cs.paddingTop) || 5;
      const paddingBottom = parseFloat(cs.paddingBottom) || 5;
      singleLineHeightRef.current = lineHeight + paddingTop + paddingBottom + 4;
    }, 0);
    return () => clearTimeout(timer);
  }, [showEditorToolbar]);

  const [isScheduleCommentModalVisible, setIsScheduleCommentModalVisible] = useState(false);
  const showScheduledCommentModal = () => setIsScheduleCommentModalVisible(true);
  const closeScheduledCommentModal = () => setIsScheduleCommentModalVisible(false);

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const closeScheduledErrorModal = () => setIsErrorModalVisible(false);

  const { data, loading, hasMore, adding, replyTo, replyToUuid, quotedComment, quotedCommentId } = state;
  const [highlightCleared, setHighlightCleared] = useState(false);

  useEffect(() => {
    setHighlightCleared(false);
  }, [highlightUuid]);

  const effectiveHighlightUuid = highlightCleared ? undefined : highlightUuid;

  let parsedText: any = null;
  if (replyTo) {
    parsedText = replyTo.delta ? deltaToHtml(replyTo.text_delta) : replyTo.text;
  }

  let parsedQuotedText: any = null;
  if (quotedComment) {
    parsedQuotedText = quotedComment.text_delta ? deltaToHtml(quotedComment.text_delta) : quotedComment.text;
  }

  const isAdmin = checkIsAdmin(user);

  const mentionableUrl =
    type === 'requests' ? `${apiRoutes.REQUESTS}/${typeUuid}/mentionables` : undefined;

  const [prefetchedMentionables, setPrefetchedMentionables] = useState<any[]>([]);

  const getSearchExcerpt = (comment: any, term: string): string => {
    const html = comment.text_delta ? deltaToHtml(comment.text_delta) : (comment.text ?? '');
    const plain = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const idx = plain.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return plain.slice(0, 100) + (plain.length > 100 ? '...' : '');
    const start = Math.max(0, idx - 40);
    const end = Math.min(plain.length, idx + term.length + 60);
    return (start > 0 ? '...' : '') + plain.slice(start, end) + (end < plain.length ? '...' : '');
  };

  const renderExcerptWithHighlight = (excerpt: string, term: string) => {
    const idx = excerpt.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return <span>{excerpt}</span>;
    return (
      <>
        {excerpt.slice(0, idx)}
        <strong className={styles.searchHighlightMatch}>{excerpt.slice(idx, idx + term.length)}</strong>
        {excerpt.slice(idx + term.length)}
      </>
    );
  };

  const handleScrollToComment = useCallback((uuid: string) => {
    const targetEl = scrollRef.current?.getScrollElement()?.querySelector<HTMLElement>(`.comment-${uuid}`);
    if (!targetEl) return;
    const { parentElement } = targetEl;
    const offsetTop = parentElement?.classList?.contains('ant-comment-nested')
      ? (parentElement.parentElement?.parentElement?.offsetTop ?? 0) + targetEl.offsetTop
      : targetEl.offsetTop;
    const scrollElement = scrollRef.current?.getScrollElement();
    if (scrollElement) scrollElement.scrollTop = offsetTop;
    targetEl.classList.add(styles.commentHighlight);
    setTimeout(() => targetEl.classList.remove(styles.commentHighlight), 2000);
  }, [scrollRef]);

  useEffect(() => {
    if (!isProjectPersonal) {
      const url =
        mentionableUrl ??
        (projectUuid
          ? `${apiRoutes.BASE_URL}/projects/${projectUuid}/mentionables`
          : `${apiRoutes.BASE_URL}/mentions/mentionables`);

      apiRequests.get(url).then((res) => {
        setPrefetchedMentionables(res.data.data ?? []);
      }).catch(() => { /* silent — falls back to debounced fetch on @ */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingScrollUuid) return;
    const exists = data.some(
      (c: any) => c.uuid === pendingScrollUuid || c.replies?.some((r: any) => r.uuid === pendingScrollUuid),
    );
    if (exists) {
      handleScrollToComment(pendingScrollUuid);
      setPendingScrollUuid(null);
    } else if (hasMore && !loading) {
      loadMore();
    }
  }, [data, pendingScrollUuid, handleScrollToComment, hasMore, loading, loadMore]);

  const handleAddImages = (uploads: UploadFileApi[]) => {
    setImages([...images, ...uploads]);
    setVisible(false);
  };

  const handleDeleteImage = (imageUuid: string) => {
    setImages([...images.filter((el) => el.response?.uuid !== imageUuid)]);
  };

  const handleSubmit = (sendAt: string | null = null) => {
    if (adding) return;

    const comment = inputRef.current?.getValue();
    const commentDelta = JSON.stringify(inputRef.current?.getQuill()?.getEditor().getContents());

    if (!comment || inputRef.current?.isEmpty()) return;

    const values = {
      text: comment,
      text_delta: commentDelta,
      related_users: getMentionUuidFromNode(comment),
      type: commentType,
      uploads: images.map((el) => el.response?.uuid),
      send_at: sendAt,
      ...(quotedCommentId ? { quoted_id: quotedCommentId } : {}),
    };

    const statedImages: UploadFileApi[] = images.map((file) => {
      return { ...file, response: { ...file.response, stated: true } as UploadS3Response };
    });

    if (replyTo && replyToUuid) {
      handleAddReply(replyToUuid, values, () => {
        setHighlightCleared(true);
        setComment('');
        setImages([]);
        setReplyTo(null, null);
        closeScheduledCommentModal();
        onFinishSubmit?.(values, statedImages);
        onInteractionOccurred?.();
      });
    } else {
      handleAddComments(values, () => {
        setHighlightCleared(true);
        setComment('');
        setImages([]);
        if (quotedCommentId) setQuotedComment(null, null);
        closeScheduledCommentModal();
        onFinishSubmit?.(values, statedImages);
        onInteractionOccurred?.();
      });
    }
    if (sendAt) message.success('Comment scheduled successfully!');
  };

  const handleClickSubmit = () => {
    handleSubmit();
  };

  const handleEmojiSelect = (emoji: { id: string; native: string }) => {
    const quill = inputRef.current?.getQuill()?.getEditor();
    if (quill) {
      const { index } = quill.getSelection(true);
      quill.insertEmbed(index, 'emoji-inline', { id: emoji.id, native: emoji.native });
      quill.setSelection(index + 1, 0);
    }
  };

  const emojiPickerContent = (
    <div ref={(ref) => {
      if (ref) ref.onclick = (e) => e.stopPropagation();
    }}>
      <EmojiPicker onSelect={handleEmojiSelect} />
    </div>
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setSearchInput(value);
    if (value) {
      setDropdownOpen(true);
      debouncedSearch.current(value);
    } else {
      setDropdownOpen(false);
      setDropdownResults([]);
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setDropdownResults([]);
    setDropdownOpen(false);
    setSearchVisible(false);
  };

  const handleDropdownResultClick = (commentUuid: string) => {
    setSearchTerm('');
    setDropdownOpen(false);
    setPendingScrollUuid(commentUuid);
  };

  const showTabs = type === 'projects' && isAdmin && !hideTabs;

  const menu = (
    <Menu>
      <Menu.Item
        key="schedule"
        onClick={() => {
          showScheduledCommentModal();
        }}
        style={{ padding: '.5rem .75rem' }}
      >
        <FieldTimeOutlined style={{ marginRight: '.5rem' }} />
        <span>Schedule send</span>
      </Menu.Item>
    </Menu>
  );

  const getCommentBody = (tabType?: string) => (
    <>
      <FileUploadModal onOk={handleAddImages} visible={visible} closeUploadModal={() => setVisible(false)} />

      <div className={styles.searchAndListWrapper}>
        {!disableSearch && (data.length > 0 || searchInput) && (
          <div className={searchVisible ? styles.searchBar : styles.searchBarFloating}>
            {searchVisible ? (
              <div className={styles.searchInputWrapper}>
                <Input
                  autoFocus
                  value={searchInput}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchInput) {
                      setDropdownOpen(true);
                      if (!dropdownResults.length) {
                        fetchDropdownResults(searchInput);
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  onBlur={() => {
                    if (!searchInput) {
                      setSearchVisible(false);
                    }
                  }}
                  placeholder="Search comments..."
                  suffix={
                    <CloseOutlined onClick={handleSearchClear} style={{ cursor: 'pointer' }} />
                  }
                  allowClear={false}
                />
                {dropdownOpen && searchInput && (
                  <div className={styles.searchDropdown}>
                    {dropdownLoading && !dropdownResults.length && (
                      <div className={styles.searchDropdownEmpty}>
                        <Spin size="small" />
                      </div>
                    )}
                    {!dropdownLoading && !dropdownResults.length && searchInput && (
                      <div className={styles.searchDropdownEmpty}>No comments found</div>
                    )}
                    {dropdownResults
                      .filter((c: any) => !c.deleted_at)
                      .map((comment: any) => (
                        <div
                          key={comment.uuid}
                          className={styles.searchDropdownItem}
                          onClick={() => handleDropdownResultClick(comment.uuid)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleDropdownResultClick(comment.uuid)}
                        >
                          <div className={styles.searchDropdownMeta}>
                            <span className={styles.searchDropdownAuthor}>{comment.creator?.name}</span>
                            <span className={styles.searchDropdownDate}>{parseDateToUSFormat(comment.created_at)}</span>
                          </div>
                          <div className={styles.searchDropdownExcerpt}>
                            {renderExcerptWithHighlight(getSearchExcerpt(comment, searchInput), searchInput)}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.searchIconContainer}>
                <Tooltip title="Search comments">
                  <SearchOutlined
                    className={styles.searchIcon}
                    onClick={() => setSearchVisible(true)}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        )}

        <Spin wrapperClassName={maxFullHeight ? styles.containerLoadingFullHeight : undefined} spinning={loading}>
        <SimpleBar
          className="bar-default"
          ref={scrollRef}
          style={{
            width: '100%',
            maxHeight: maxFullHeight ? '100%' : maxHeight ?? '80vh',
            minHeight: !loading && !data.length ? '170px' : minHeight,
          }}
        >
          <div style={{ width: '100%', overflowX: 'hidden', padding: 0, paddingRight: 15 }}>
            {hasMore && (
              <Row justify="center">
                {loading ? (
                  <Text className="primary">Loading...</Text>
                ) : (
                  <Button className="primary" onClick={loadMore} type="text" style={{ marginBottom: 5 }}>
                    Load more
                  </Button>
                )}
              </Row>
            )}

            {data.map((comment) => (
              <CommentCard
                key={comment.uuid}
                comment={comment}
                targetCommentId={effectiveHighlightUuid}
                projectUuid={projectUuid}
                typeUuid={typeUuid}
                scrollingContainer={scrollingContainer}
                mentionTypes={mentionTypes}
                mentionableUrl={mentionableUrl}
                prefetchedMentionables={prefetchedMentionables}
                user={user}
                type={type}
                canComment={canComment}
                searchTerm={searchInput}
                onReplyTo={setReplyTo}
                onQuoteTo={setQuotedComment}
                onScrollToComment={handleScrollToComment}
                onImgDelete={handleDeleteUpload}
                onAddReaction={(uuid: string, reaction: any) => {
                  setHighlightCleared(true);
                  handleAddReaction(uuid, reaction);
                  onInteractionOccurred?.();
                }}
                onRemoveReaction={handleRemoveReaction}
                onAddReplyReaction={handleAddReplyReaction}
                onRemoveReplyReaction={handleRemoveReplyReaction}
                onDelete={async (uuid: string) => {
                  await handleDeleteComment(uuid);
                  onFilesDelete?.(comment.uploads.map((item: UploadResponse) => item.uuid));
                }}
                onUpdate={handleUpdateComment}
                onUpdateSchedule={handleUpdateScheduleTime}
                onImageModalChanged={onImageModalChanged}
                commentReplies={comment.replies.map((reply: any) => {
                  return (
                    <CommentCard
                      key={reply.uuid}
                      parentEl={comment}
                      comment={reply}
                      targetCommentId={effectiveHighlightUuid}
                      projectUuid={projectUuid}
                      typeUuid={typeUuid}
                      scrollingContainer={scrollingContainer}
                      mentionTypes={mentionTypes}
                      mentionableUrl={mentionableUrl}
                      prefetchedMentionables={prefetchedMentionables}
                      user={user}
                      type={type}
                      canComment={canComment}
                      searchTerm={searchInput}
                      onReplyTo={setReplyTo}
                      onQuoteTo={setQuotedComment}
                      onImgDelete={handleDeleteUpload}
                      onAddReaction={(parentUuid, replyUuid, reaction) => {
                        setHighlightCleared(true);
                        handleAddReplyReaction(parentUuid, replyUuid, reaction);
                        onInteractionOccurred?.();
                      }}
                      onRemoveReaction={handleRemoveReplyReaction}
                      onDelete={async (uuid, parentUuid) => {
                        await handleDeleteReply(uuid, parentUuid);
                        onFilesDelete?.(comment.uploads.map((item: UploadResponse) => item.uuid));
                      }}
                      onUpdate={handleUpdateReply}
                      onUpdateSchedule={handleUpdateScheduleTime}
                      onImageModalChanged={onImageModalChanged}
                    />
                  );
                })}
              />
            ))}

            {data.length === 0 && !loading && <Empty style={{ padding: '12px 10px 24px' }} />}
          </div>
        </SimpleBar>
      </Spin>
      </div>

      {canComment && (
        <Space size={4} style={{ width: '100%', padding: '10px 0' }} direction="vertical">
          {!hideTemplates && (
            <TemplateCommentContainer
              onTemplateClick={(template) => {
                setComment(
                  replaceUserInTemplate
                    ? template.content
                        .replaceAll('data-value="name"', `data-value="${replaceUserInTemplate.name}"`)
                        .replaceAll('data-id=""', `data-id="${replaceUserInTemplate.uuid}"`)
                        .replaceAll(
                          '<span class="ql-mention-denotation-char">@</span>name',
                          `<span class="ql-mention-denotation-char">@</span>${replaceUserInTemplate.name}`,
                        )
                    : template.content,
                );
              }}
            />
          )}

          {type === 'projects' && isAdmin && (tabType === 'all' || hideTabs) && (
            <Select
              onChange={(value) => setCommentType(value)}
              disabled={replyTo?.type === 'private'}
              options={[
                {
                  label: 'Private note',
                  value: 'private',
                },
                {
                  label: 'Comment',
                  value: 'comment',
                },
                {
                  label: 'Call log',
                  value: 'call_log',
                },
                {
                  label: 'Email log',
                  value: 'email_log',
                },
                {
                  label: 'Summary',
                  value: 'summary',
                },
              ]}
              style={{ width: 150 }}
              value={commentType}
            />
          )}

          <div ref={chatInputRef} className={styles.addCommentContainer}>
            <div className={`${styles.chatInputWrapper}${dragState ? ` drag-overlay-parent drag-overlay-parent--${dragState}` : ''}`}>
              {replyTo && (
                <div className={styles.replyTo}>
                  <div className={styles.line} />
                  <Row justify="space-between" gutter={16} wrap={false} align="middle">
                    <Col flex={1}>
                      <strong>{replyTo.creator?.name}</strong>

                      <RichTextEditor
                        key={replyTo.uuid}
                        className="comment-styles resume"
                        initialValue={mentionCommentParser(parsedText)}
                        readonly
                        noContainer
                      />
                    </Col>
                    <Col>
                      <CloseOutlined onClick={() => setReplyTo(null, null)} />
                    </Col>
                  </Row>
                </div>
              )}

              {quotedComment && (
              <div className={styles.replyTo}>
                <div className={styles.line} />
                <Row justify="space-between" gutter={16} wrap={false} align="middle">
                  <Col flex={1}>
                    <strong><FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />{quotedComment.creator?.name}</strong>

                    <RichTextEditor
                      key={quotedComment.uuid}
                      className="comment-styles resume"
                      initialValue={mentionCommentParser(parsedQuotedText)}
                      readonly
                      noContainer
                    />
                  </Col>
                  <Col>
                    <CloseOutlined onClick={() => setQuotedComment(null, null)} />
                  </Col>
                </Row>
              </div>
            )}

              <div className={`${styles.chatInputEditorRow}${isEditorMultiline ? ` ${styles.chatInputEditorRowMultiline}` : ''}`}>
                <RichTextEditor
                  ref={inputRef}
                  includeToolbar={showEditorToolbar}
                  includeMention={!isProjectPersonal}
                  projectId={projectUuid}
                  mentionableUrl={mentionableUrl}
                  placeholder="Add a comment [CMD/CTRL + Enter to send]"
                  className={`text-sm chat-input ${textEditorClassName}`}
                  scrollingContainer={scrollingContainer}
                  progressBar="top"
                  imageAsData64
                  hideDropOverlay
                  onImageUploader={async (upload) => {
                    setImages((prev) => [...prev, upload]);
                  }}
                  mentionTypes={
                    commentType === 'private' && mentionTypes?.includes('contact')
                      ? mentionTypes?.filter((item) => item !== 'contact')
                      : mentionTypes
                  }
                  onLoadingImage={setLoadingImages}
                  onShortEnterKey={() => handleSubmit(null)}
                  onImageModalChanged={onImageModalChanged}
                  onDragStateChange={setDragState}
                  onChange={() => {
                    const isEmpty = inputRef.current?.isEmpty?.() ?? true;
                    setIsEditorEmpty(isEmpty);
                    const scrollHeight = inputRef.current?.getQuill()?.getEditor()?.root?.scrollHeight ?? 0;
                    setIsEditorMultiline((prev) => {
                      if (isEmpty) return false;
                      if (prev) return true;
                      return scrollHeight > singleLineHeightRef.current + 5;
                    });
                    if (!isEmpty) {
                      requestAnimationFrame(() => {
                        let node = bottomAnchorRef.current?.parentElement ?? null;
                        while (node && node !== document.body) {
                          if (node.scrollHeight > node.clientHeight) {
                            node.scrollTop = node.scrollHeight;
                            break;
                          }
                          node = node.parentElement;
                        }
                      });
                    }
                  }}
                />

                <div className={styles.chatInputActions}>
                  <span className={styles.chatInputToolbarToggle}>
                    <Tooltip title="Formatting options">
                      <Button
                        type="text"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        icon={<MdOutlineTextFormat style={{ fontSize: '18px' }} />}
                        onClick={() => setShowEditorToolbar(!showEditorToolbar)}
                      />
                    </Tooltip>
                  </span>

                  <Tooltip title="Add emoji">
                    <Popover
                      content={emojiPickerContent}
                      trigger="click"
                      placement="bottomLeft"
                      getPopupContainer={() => document.body}
                      overlayClassName={`no-padding-popover ${styles.emojiPortal}`}
                    >
                      <Button
                        type="text"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        icon={<FaRegSmile style={{ fontSize: '18px' }} />}
                      />
                    </Popover>
                  </Tooltip>

                  <Tooltip title="Upload file">
                    <Button
                      type="text"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      icon={<UploadOutlined style={{ fontSize: '18px' }} />}
                      onClick={() => setVisible(true)}
                    />
                  </Tooltip>
                </div>
              </div>

              {images.length > 0 && (
                <Space wrap style={{ paddingTop: 4 }}>
                  {images.map((el) => {
                    return (
                      <div
                        style={{
                          padding: '4px 8px',
                          background: '#fff',
                          borderRadius: 12,
                        }}
                        key={el.response?.uuid}
                      >
                        <Space align="center">
                          {el.name}
                          <CloseOutlined onClick={() => handleDeleteImage(el.response?.uuid ?? '')} />
                        </Space>
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>

            <Button.Group style={{ border: 'none', gap: 2, marginBottom: 6 }}>
              <Dropdown disabled={isEditorEmpty} overlay={menu} placement="bottomRight" trigger={['hover']}>
                <Button
                  type="primary"
                  icon={<FaCaretDown style={{ fontSize: 12 }} />}
                  style={{ borderRadius: '16px 0 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
              </Dropdown>
              <Button
                style={{ borderRadius: '0 16px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50 }}
                loading={adding}
                disabled={loadingImages > 0 || isEditorEmpty}
                onClick={handleClickSubmit}
                type="primary"
                icon={<IoSendSharp />}
              />
            </Button.Group>
          </div>
          <div ref={bottomAnchorRef} />
        </Space>
      )}

      <CommentSchedule visible={isScheduleCommentModalVisible} onClose={closeScheduledCommentModal} onSubmit={handleSubmit} />
      <CommendScheduleError visible={isErrorModalVisible} onClose={closeScheduledErrorModal} />
    </>
  );

  return showTabs ? (
    <Tabs
      tabBarExtraContent={
        <Tooltip title="Refresh" placement="left">
          <Button type="text" icon={<ReloadOutlined />} onClick={reloadComment} />
        </Tooltip>
      }
      activeKey={tabView}
      onChange={(view) => {
        setTabView(view);
        if (view === 'all') {
          setCommentType('comment');
        } else {
          setCommentType(view);
        }
      }}
    >
      {[
        {
          label: 'All',
          value: 'all',
        },
        {
          label: 'Comment',
          value: 'comment',
        },
        {
          label: 'Call log',
          value: 'call_log',
        },
        {
          label: 'Email log',
          value: 'email_log',
        },
        {
          label: 'Private note',
          value: 'private',
        },
        {
          label: 'Summary',
          value: 'summary',
        },
      ].map((el) => {
        return (
          <Tabs.TabPane tab={el.label} key={el.value}>
            {getCommentBody(el.value)}
          </Tabs.TabPane>
        );
      })}
    </Tabs>
  ) : (
    getCommentBody()
  );
};

export default Comments;
